// src/pages/receipts/index.jsx

import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import {
  IoAddOutline,
  IoDocumentTextOutline,
  IoSearch,
  IoSettingsOutline
} from "react-icons/io5";
import { query, collection, onSnapshot, setDoc, doc } from "firebase/firestore";
import OutletHead from "../../../components/common/outlet-head";
import { db } from "./../../../firebase";
import DataTable from "../../../components/receipts/datatables";
import { useAuth } from "../../../context/AuthContext";
import CreateReceipt from "../../../components/receipts/form";
import { InvoiceNumber } from "invoice-number";
import { toast } from "react-toastify";

function Receipts() {
  const { user } = useAuth();
  const { bid } = user.claims;

  const [isLoading, setIsLoading] = useState(true);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [filteredReceipts, setFilteredReceipts] = useState([]);

  const [selectedParty, setSelectedParty] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);

  const [receiptData, setReceiptData] = useState({
    paymentDate: new Date(),
    paymentRemark: "",
    paymentMode: "Cash",
    amountReceived: "",
    paymentReferenceNumber: "",
    chequeDate: null,
    paymentReceivedFrom: ""
  });

  const [invoices, setInvoices] = useState([]);
  const [updatedInvoiceIDs, setUpdatedInvoiceIDs] = useState([]);

  const [inputError, setInputError] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    setIsLoading(true);

    const q2 = query(collection(db, "Businesses", bid, "Receipts"));

    const unsubscribe = onSnapshot(
      q2,
      (querySnapshot) => {
        const tempData = [];
        querySnapshot.forEach((docSnap) => {
          tempData.push({ id: docSnap.id, ...docSnap.data() });
        });

        tempData.sort((a, b) => {
          const aDt =
            a.date && a.date.seconds
              ? new Date(a.date.seconds * 1000)
              : a.date
                ? new Date(a.date)
                : null;
          const bDt =
            b.date && b.date.seconds
              ? new Date(b.date.seconds * 1000)
              : b.date
                ? new Date(b.date)
                : null;

          const aMs = aDt ? aDt.getTime() : 0;
          const bMs = bDt ? bDt.getTime() : 0;

          return bMs - aMs;
        });

        setReceipts(tempData);
        setFilteredReceipts(tempData);
        setIsLoading(false);
      },
      (error) => {
        console.error("Receipts snapshot error:", error);
        setIsLoading(false);
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [bid]);

  async function handleCreateReceipt() {
    try {
      setCreating(true);
      setInputError((prev) => ({ ...prev, general: "" }));

      if (!selectedParty || !selectedBooking) {
        setInputError((prev) => ({
          ...prev,
          general: "Select Party and Booking first."
        }));
        toast.error("Select Party and Booking first.");
        setCreating(false);
        return;
      }

      if (!receiptData.paymentDate) {
        setInputError((prev) => ({
          ...prev,
          paymentDate: "Payment date is required."
        }));
        toast.error("Payment date is required.");
        setCreating(false);
        return;
      }

      const amt = parseFloat(receiptData.amountReceived || 0);
      if (!amt || amt <= 0) {
        setInputError((prev) => ({
          ...prev,
          amountReceived: "Enter valid amount."
        }));
        toast.error("Enter valid amount.");
        setCreating(false);
        return;
      }

      if (!Array.isArray(updatedInvoiceIDs) || updatedInvoiceIDs.length === 0) {
        setInputError((prev) => ({
          ...prev,
          amountReceived: "No instalments selected / allocated."
        }));
        toast.error("No instalments selected / allocated.");
        setCreating(false);
        return;
      }

      const d = new Date();
      const year = d.getFullYear();
      let month = d.getMonth() + 1;
      month = month.toString().length === 1 ? `0${month}` : month;

      let reptID = `REPT${year}${month}-${(
        "000" + (receipts ? receipts.length : 0)
      ).slice(-4)}`;
      reptID = InvoiceNumber.next(reptID);

      const payload = {
        createdOn: new Date(),
        date: receiptData.paymentDate,
        amountReceived: amt,
        partyID: selectedParty.id,
        bookingID: selectedBooking.id,
        description: receiptData.paymentRemark || "",
        invoiceIDs: Array.isArray(updatedInvoiceIDs)
          ? updatedInvoiceIDs
          : [],
        paymentDetails: {
          chequeDate: receiptData.chequeDate || null,
          dueDate: null,
          paymentMode: receiptData.paymentMode,
          paymentReceivedFrom: receiptData.paymentReceivedFrom || null,
          paymentReferenceNumber:
            receiptData.paymentReferenceNumber || ""
        },
        meta: { createdBy: user?.uid || null },
        taxRate: selectedBooking?.taxRate ?? 0
      };

      await setDoc(
        doc(db, "Businesses", bid, "Receipts", reptID),
        payload
      );

      setSelectedParty(null);
      setSelectedBooking(null);
      setInvoices([]);
      setUpdatedInvoiceIDs([]);
      setReceiptData({
        paymentDate: new Date(),
        paymentRemark: "",
        paymentMode: "Cash",
        amountReceived: "",
        paymentReferenceNumber: "",
        chequeDate: null,
        paymentReceivedFrom: ""
      });
      setInputError({});
      setShowReceiptModal(false);

      toast.success(
        "Receipt created successfully — backend will apply allocations."
      );
      setCreating(false);
    } catch (error) {
      console.error("Receipt [Error] : ", error);
      toast.error("Failed to create receipt. See console.");
      setCreating(false);
    }
  }

  const handleSearchChange = (val) => {
    const term = val || "";
    setSearchTerm(term);

    if (!term) {
      setFilteredReceipts(receipts);
      return;
    }

    const lower = term.toLowerCase();

    const filtered = receipts.filter((r) => {
      const idMatch =
        r.id && r.id.toLowerCase().includes(lower);
      const bookingMatch =
        r.bookingID && r.bookingID.toLowerCase().includes(lower);

      const invoiceMatch =
        Array.isArray(r.invoiceIDs) &&
        r.invoiceIDs.some((i) => {
          const invId =
            i.id && typeof i.id.toLowerCase === "function"
              ? i.id.toLowerCase()
              : "";
          const invLegacy =
            i.invoiceId &&
              typeof i.invoiceId.toLowerCase === "function"
              ? i.invoiceId.toLowerCase()
              : "";
          return invId.includes(lower) || invLegacy.includes(lower);
        });

      return idMatch || bookingMatch || invoiceMatch;
    });

    setFilteredReceipts(filtered || []);
  };

  const openReceiptModal = () => {
    setShowReceiptModal(true);
    setInputError({});
  };

  const closeReceiptModal = () => {
    setShowReceiptModal(false);
    setTimeout(() => {
      setSelectedParty(null);
      setSelectedBooking(null);
      setInvoices([]);
      setUpdatedInvoiceIDs([]);
      setReceiptData({
        paymentDate: new Date(),
        paymentRemark: "",
        paymentMode: "Cash",
        amountReceived: "",
        paymentReferenceNumber: "",
        chequeDate: null,
        paymentReceivedFrom: ""
      });
      setInputError({});
    }, 250);
  };

  return (
    <section className="section">
      <OutletHead
        left={<h5 className="mb-0 fw-600">Receipts</h5>}
        right={
          <div className="d-flex align-items-center">
            <Button
              variant="outline-primary"
              type="button"
              className="py-2 px-4 me-3"
            >
              <IoDocumentTextOutline size={18} />
              <span className="ps-2">Export</span>
            </Button>
            <Button
              variant="light"
              type="button"
              className="border py-2 px-4"
            >
              <IoSettingsOutline size={20} />
              <span className="ps-2">Settings</span>
            </Button>
          </div>
        }
      />
      <div className="p-3 border-top">
        <div className="row">
          <div className="col-lg-12">
            {isLoading ? (
              <div className="data-loading-container">
                <div className="loader">
                  <div className="spinner" />
                </div>
              </div>
            ) : (
              <div className="mb-5">
                <div className="pb-3">
                  <Form>
                    <div className="row">
                      <div className="col-8">
                        <div className="row">
                          <div className="col-lg-6 col-md-6 col-12">
                            <div className="position-relative">
                              <div className="position-absolute left-0 top-0 bottom-0 ps-3 d-flex align-items-center justify-content-center">
                                <IoSearch
                                  size={20}
                                  color={`var(--bs-placeholder)`}
                                />
                              </div>
                              <Form.Control
                                type="text"
                                placeholder="Search receipt by receipt id, invoice id or booking id"
                                onChange={(e) =>
                                  handleSearchChange(e.target.value)
                                }
                                className="ps-5"
                                value={searchTerm}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-4 d-flex justify-content-end">
                        <Button
                          variant="primary"
                          type="button"
                          className="py-2 px-3"
                          onClick={openReceiptModal}
                        >
                          <IoAddOutline size={21} />
                          <span className="ps-2">Create Receipt</span>
                        </Button>
                      </div>
                    </div>
                  </Form>
                </div>

                <DataTable
                  columns={[
                    "Date",
                    "Receipt ID",
                    "Invoice ID",
                    "Booking ID",
                    "Party Name",
                    "Amount"
                  ]}
                  data={filteredReceipts}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        show={showReceiptModal}
        onHide={closeReceiptModal}
        size={selectedBooking && selectedBooking ? "xl" : "lg"}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Payments</Modal.Title>
        </Modal.Header>

        {showReceiptModal && (
          <CreateReceipt
            receiptData={receiptData}
            setReceiptData={setReceiptData}
            selectedParty={selectedParty}
            setSelectedParty={setSelectedParty}
            selectedBooking={selectedBooking}
            setSelectedBooking={setSelectedBooking}
            invoices={invoices}
            setInvoices={setInvoices}
            setUpdatedInvoiceIDs={setUpdatedInvoiceIDs}
            inputError={inputError}
            setInputError={setInputError}
          />
        )}

        <Modal.Footer className="justify-content-between">
          <Button
            variant="outline-dark"
            onClick={closeReceiptModal}
            className="px-5 py-2 btn-width"
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="px-5 py-2 btn-width"
            onClick={handleCreateReceipt}
            disabled={creating}
          >
            {creating ? (
              <span>
                <Spinner size="sm" className="me-1" />
              </span>
            ) : (
              <span>Create</span>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}

export default Receipts;